import React, { useEffect, useState } from 'react';
import {
    getLiveChatService,
    INACTIVE_LIVE_CHAT_TIMEOUT_MINUTES,
    LIVE_CHAT_ERROR_JWT_UNDEFINED,
    LIVE_CHAT_ERROR_IDENTITY_NOT_VERIFIED,
    LiveChatConnectionType,
    OnLiveChatActiveEvent,
    OnLiveChatClosedEvent,
    OnLiveChatClosedInTabEvent,
    OnLiveChatInactiveEvent,
    OnLiveChatMessageEvent,
} from '../liveChatService';
import { TransferLiveAgentFeedback } from '../jsxElements';
import {
    clearLiveChatCookies,
    format,
    getCurrentSessionToken,
    getLiveChatJwt,
    getWatermark,
    isMobileBrowser,
    isUnified,
    sessionStorageAvailable,
    setLiveChatJwt,
    clearRehydrationData,
} from '../helpers';
import { useConfig, useDisableTextSend, useInputMode } from '../hooks';
import { LiveChatSessionInfoBubble } from '../jsxElements/LiveChatSessionInfoBubble';
import { SelectActionModal } from '../jsxElements/SelectActionModal';
import {
    getConversationId,
    getCorrelationId,
    logEvent,
    logException,
} from '../logging';
import {
    addLinkClickEvents,
    LiveChatAnalytics,
    LiveChatAnalyticsDisposer,
} from '../analytics/LiveChatAnalytics';
import { AmpFlagSet } from '../amplitude';
import {
    genesysSignInWithHealthSystemIDTokenRedirect,
    validateAuthCodeIdentity,
} from '../genesysService';
import liveChatConfig from '../resources/liveChatConfig.json';
import useLiveChatState, { LiveChatState } from '../hooks/useLiveChatState';
import {
    LIVE_CHAT_END_CHAT_INTENT,
    LIVE_CHAT_HEARTBEAT_INTENT,
    LIVE_CHAT_UNSUPPORTED_INTENT,
} from '../intents';

const GENESYS_OAUTH_ENDPOINT_PATH =
    '/api/v2/webdeployments/token/oauthcodegrantjwtexchange';
const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

export interface LiveChatAttachmentData {
    removeBubble: boolean;
    hideAvatar: boolean;
    noMargin: boolean;
    deploymentId: string;
    token: string;
    estimatedWaitTimeSeconds: number;
    EPI: string;
    firstName: string;
    liveChatAnalyticsPrefix: string;
    liveChatTenantName: string;
    flow: string;
    epicRegion?: string;
}

type LiveChatArgs = {
    data: LiveChatAttachmentData;
    featureFlags: AmpFlagSet;
    sendEvent: (name: string, value: any) => void;
    sendPostBack: (value: any) => void;
    mostRecentMessage: boolean;
};

export const LiveChat: React.FC<LiveChatArgs> = ({
    data,
    featureFlags,
    sendEvent,
    sendPostBack,
    mostRecentMessage,
}) => {
    const {
        deploymentId,
        token,
        liveChatAnalyticsPrefix,
        liveChatTenantName,
        flow,
    } = data;
    const liveChatService = getLiveChatService();
    const [conversation, setConversation] = useState([]);
    const { setInputMode } = useInputMode();
    const { setDisableTextSend } = useDisableTextSend();
    const config = useConfig();
    const authorizeWebviewUrl =
        config.genesys[liveChatTenantName].authorizeWebviewUrl;
    const genesysApiUrl = config.genesys[liveChatTenantName].genesysApiEndpoint;
    const authorizeTokenUrl =
        config.genesys[liveChatTenantName].authorizeTokenUrl;
    const tier = config.tier;
    const genesysAuthCode = window?.genesysAuth?.authCode;
    const genesysCodeVerifier = window?.genesysAuth?.codeVerifier;
    const genesysRedirectUri = window?.genesysAuth?.redirectUri;
    const expectedConnectionInstant =
        Date.now() + 1000 * data.estimatedWaitTimeSeconds;

    const redirect = async () => {
        // We can't access session storage by default in web, chrome incognito because we run in an iframe
        if (!sessionStorageAvailable() || !liveChatTenantName) {
            sendPostBack(LIVE_CHAT_UNSUPPORTED_INTENT);
            return;
        }

        const lastActivityId = getWatermark();
        setInputMode('disabled');
        sessionStorage.setItem('lastActivityId', lastActivityId);
        sessionStorage.setItem('sessionToken', getCurrentSessionToken() || '');

        if (isUnified()) {
            genesysSignInWithHealthSystemIDTokenRedirect(
                authorizeWebviewUrl,
                liveChatTenantName
            );
        } else {
            parent.window.postMessage(
                { type: 'genesyslogin', liveChatTenantName },
                '*'
            );
        }
    };

    const [currentState, setCurrentState] = useLiveChatState(token);

    useEffect(() => {
        if (currentState === LiveChatState.notStarted) {
            setCurrentState(LiveChatState.started);
        }
    }, []);

    useEffect(() => {
        // Syncs liveChatService.state with currentState so the liveChatService can work with up-to-date state values.
        liveChatService.state = currentState;
    }, [currentState]);

    useEffect(
        function toggleUserInputMode() {
            // If the LiveChat is active but an agent has not connected, disable the user input.
            const isInputDisabled = currentState === LiveChatState.started;
            setDisableTextSend(isInputDisabled);
            setInputMode(isInputDisabled ? 'disabled' : 'enabled');
        },
        [currentState]
    );

    useEffect(() => {
        const heartbeat = () => {
            if (currentState !== LiveChatState.stopped) {
                sendPostBack(LIVE_CHAT_HEARTBEAT_INTENT);
            }
        };
        const interval = setInterval(heartbeat, FIVE_MINUTES_IN_MS);
        return () => {
            clearInterval(interval);
        };
    }, [currentState]);

    useEffect(() => {
        const listener = liveChatService.eventsStream.listen((event) => {
            if (event instanceof OnLiveChatClosedEvent) {
                if (currentState === LiveChatState.stopped) {
                    // Prevents multiple close events from firing and disposes of this duplicate listener.
                    listener.dispose();
                    return;
                }
                setCurrentState(LiveChatState.stopped);
                clearLiveChatCookies(tier);
                sendPostBack(LIVE_CHAT_END_CHAT_INTENT);
                listener.dispose();
            } else if (event instanceof OnLiveChatClosedInTabEvent) {
                listener.dispose();
            } else if (event instanceof OnLiveChatInactiveEvent) {
                setCurrentState(LiveChatState.inactive);
            } else if (event instanceof OnLiveChatActiveEvent) {
                setCurrentState(LiveChatState.active);
            } else if (event instanceof OnLiveChatMessageEvent) {
                try {
                    if (event.message.class === 'SessionResponse') {
                        // TODO: We can use this case to send over user info
                    } else if (
                        event.isUserMessage &&
                        event.message.body.direction === 'Outbound'
                    ) {
                        const newConversation = JSON.parse(
                            JSON.stringify(conversation)
                        );
                        newConversation.push({
                            text: event.message.body.text,
                            from: 'agent',
                        });
                        setConversation(newConversation);
                        setTimeout(() => {
                            addLinkClickEvents(
                                liveChatAnalyticsPrefix,
                                liveChatTenantName
                            );
                        }, 1000);
                    } else if (
                        event.isUserMessage &&
                        event.message.body.direction === 'Inbound' &&
                        !event.isSystemMessage
                    ) {
                        const newConversation = JSON.parse(
                            JSON.stringify(conversation)
                        );
                        newConversation.push({
                            text: event.message.body.text,
                            from: 'user',
                        });
                        setConversation(newConversation);
                    }
                } catch (error) {
                    console.log('Error in receive message');
                    console.log(error);
                }
            }
        });

        return () => {
            listener.dispose();
        };
    }, [conversation, currentState]);

    useEffect(() => {
        const fetchGenesysToken = async () => {
            const payload = {
                deploymentId: deploymentId,
                oauth: {
                    code: genesysAuthCode,
                    codeVerifier: genesysCodeVerifier,
                    redirectUri: genesysRedirectUri,
                },
            };
            const url = `${genesysApiUrl}${GENESYS_OAUTH_ENDPOINT_PATH}`;
            try {
                const resp = await window.fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });
                const result = await resp.json();
                if (resp.ok) {
                    return result;
                }
                throw new Error(JSON.stringify(result));
            } catch (error: any) {
                logException(
                    'LiveChat - Fail to fetch and parse Genesys token',
                    {
                        exception: error,
                    }
                );
            }
            return {};
        };
        const initializeLiveChatService = async () => {
            let jwt = getLiveChatJwt(featureFlags); // Check for JWT
            let connectionType = LiveChatConnectionType.reconnect;
            if (!jwt) {
                if (!isUnified()) {
                    const result = await validateAuthCodeIdentity(
                        authorizeTokenUrl,
                        liveChatTenantName,
                        genesysAuthCode,
                        genesysCodeVerifier,
                        genesysRedirectUri
                    );
                    if (!result) {
                        logException('LiveChat - Invalid Auth Code Identity', {
                            exception: new Error('InvalidAuthCode'),
                        });
                        liveChatService.setError(
                            LIVE_CHAT_ERROR_IDENTITY_NOT_VERIFIED
                        );
                        clearRehydrationData(tier);
                        return;
                    }
                }
                const genesysAccessTokenData = await fetchGenesysToken();
                jwt = genesysAccessTokenData.jwt;
                connectionType = LiveChatConnectionType.connect;
                // eslint-disable-next-line no-restricted-globals
                parent.window.postMessage('liveChatStarted', '*');
            }

            if (!jwt) {
                liveChatService.setError(LIVE_CHAT_ERROR_JWT_UNDEFINED);
                clearRehydrationData(tier);
            } else {
                liveChatService.initialize(
                    featureFlags,
                    deploymentId,
                    token,
                    connectionType,
                    jwt,
                    sendEvent,
                    {
                        firstName: data.firstName,
                        epicRegion: data.epicRegion || '',
                        EPI: data.EPI,
                        correlationId: getCorrelationId(),
                        conversationId: getConversationId(),
                        isMobileBrowser: isMobileBrowser(),
                    }
                );
                setLiveChatJwt(featureFlags, jwt, tier);
            }
        };

        let disposeAnalyticsTracker: LiveChatAnalyticsDisposer | undefined;
        if (genesysAuthCode || getLiveChatJwt(featureFlags)) {
            // Set up analytics
            const liveChatAnalytics = new LiveChatAnalytics(
                liveChatService.eventsStream,
                liveChatAnalyticsPrefix,
                flow,
                expectedConnectionInstant,
                token,
                liveChatTenantName
            );
            disposeAnalyticsTracker = liveChatAnalytics.track();

            initializeLiveChatService();
        } else if (
            mostRecentMessage ||
            currentState === LiveChatState.active ||
            currentState === LiveChatState.started
        ) {
            redirect();
        }
        return () => {
            if (disposeAnalyticsTracker !== undefined) {
                disposeAnalyticsTracker();
            }
        };
    }, [deploymentId, token]);

    if (!genesysAuthCode && !getLiveChatJwt(featureFlags)) {
        return (
            <div>
                <p>{liveChatConfig.providence.connecting}</p>
            </div>
        );
    }

    const liveChatidleActions = [
        {
            title: liveChatConfig.providence.idleContinue,
            onClick: () => liveChatService.keepAlive(),
        },
        {
            title: liveChatConfig.providence.idleClose,
            onClick: () => {
                logEvent({ name: 'LiveChatUserClose' });
                liveChatService.userClose();
            },
        },
    ];

    return (
        <div>
            <p>{liveChatConfig.providence.connecting}</p>
            <TransferLiveAgentFeedback
                expectedConnectionInstant={expectedConnectionInstant}
                liveChatEnded={currentState === LiveChatState.stopped}
                currentState={currentState}
                featureFlags={featureFlags}
            />
            {currentState === LiveChatState.stopped && (
                <LiveChatSessionInfoBubble>
                    {liveChatConfig.providence.liveSessionEnded}
                </LiveChatSessionInfoBubble>
            )}
            <SelectActionModal
                open={currentState === LiveChatState.inactive}
                title={liveChatConfig.providence.continueConversation}
                body={format(liveChatConfig.providence.userIdle, {
                    minutes: INACTIVE_LIVE_CHAT_TIMEOUT_MINUTES,
                })}
                actions={liveChatidleActions}
            />
        </div>
    );
};

export default LiveChat;
